import {
    AVAILABLE_CT5_VM_TYPES,
    CT_TYPES,
    DIRECT_PAYMENT_MACHINE_TYPES,
    MACHINE_TYPES,
    PAYMENT_PROVIDER,
    PULSE_MACHINE_TYPES,
    SOFTWARE_VERSION_SIX_TIMER,
    TIMER_START_VALUE,
} from '../constants';
import { VendingMachine } from '../types';

/**
 * The single place allowed to read `type_id`, `ct_type`, `sfVersion` and
 * `device_serie`. Everything else works with the named capabilities below.
 * See `.claude/rules/branching-logic.md` (Rule 2).
 */
export interface VmInfo {
    qr?: string;
    isCT10: boolean;
    isCT5: boolean;
    isConnected: boolean;
    isAllowed: boolean;
    isFridge: boolean;
    isFridgeSmart: boolean;
    isPulse: boolean;
    isPopcorn: boolean;
    /** No product to pick — the machine takes an amount and dispenses itself. */
    isDirectPayment: boolean;
    /**
     * On the final wait-screen poll, a charged order whose device still reports
     * "product selected" counts as a completed vend for this machine.
     * A grace rule: it only applies on the last tick, never earlier.
     */
    treatsProductSelectedAsSuccess: boolean;
    /**
     * Same grace rule, for machines whose device reports "connected" rather than
     * "product selected" when it has finished dispensing.
     */
    treatsConnectedAsSuccess: boolean;
    /**
     * A charged order whose device reports "session closed" is still a sale, not a
     * refund in progress: on firmware 6.x with Tranzzo, payment-service sells the
     * order on the dispense result and web0 no longer refunds a sold order when the
     * session closes. The wait screen keeps polling instead of showing a refund,
     * and on the final poll the order counts as a completed vend.
     */
    treatsSessionClosedAsSale: boolean;
    /** Countdown for the connection screen, in seconds. */
    connectTimeoutSec: number;
    /** Countdown for the product-selection screen, in seconds. */
    selectTimeoutSec: number;
    /** Countdown for the payment screen, in seconds. */
    paymentTimeoutSec: number;
    language_code?: string;
    isFeedbackEnabled?: boolean;
    hasFiscalReceipt?: boolean;
}

/** Firmware 6.x speaks its own MQTT dialect; module-private like `sfVersion` itself. */
const isFirmware6x = (sfVersion: string | null): boolean =>
    Boolean(sfVersion && sfVersion.startsWith('6.'));

/**
 * Timer duration for one screen.
 *
 * Firmware 6.x on board revision 'A' keeps its own session clock, so the app has
 * to follow it: non-payment screens use the fixed per-screen value and the payment
 * screen is capped at 60s. Everything else runs on the app's own defaults.
 *
 * Module-private on purpose — `sfVersion` and `device_serie` must not leave this
 * file. The three exposed `*TimeoutSec` capabilities are the public surface.
 */
const resolveTimerSeconds = (
    sfVersion: string | null,
    deviceSerie: string | null,
    screenValue: number,
    vmTimeout: number,
    isPayment: boolean,
): number => {
    if (isFirmware6x(sfVersion) && deviceSerie === 'A') {
        if (isPayment) {
            return vmTimeout > 60 ? 60 : vmTimeout;
        }
        return screenValue;
    }
    return isPayment ? vmTimeout : TIMER_START_VALUE;
};

export const getVmDetails = (vm: VendingMachine): VmInfo => {
    const result: VmInfo = {
        qr: vm.qr,
        isCT10: false,
        isCT5: false,
        isConnected: vm.status_connected === 1,
        isAllowed: vm.allowed === 1,
        isFridge: vm.type_id === MACHINE_TYPES.FRIDGE,
        isFridgeSmart: vm.type_id === MACHINE_TYPES.FRIDGE_SMART,
        isPulse: PULSE_MACHINE_TYPES.includes(vm.type_id),
        isPopcorn: vm.type_id === MACHINE_TYPES.POPCORN_PULSE_MACHINE,
        isDirectPayment: DIRECT_PAYMENT_MACHINE_TYPES.includes(vm.type_id),
        treatsProductSelectedAsSuccess:
            !DIRECT_PAYMENT_MACHINE_TYPES.includes(vm.type_id) &&
            (vm.ct_type === CT_TYPES.NULL || vm.ct_type === CT_TYPES.CT5),
        treatsConnectedAsSuccess:
            (vm.type_id === MACHINE_TYPES.SNACK_MACHINE_PULS ||
                vm.type_id === MACHINE_TYPES.WATER_MACHINE_PULS) &&
            vm.ct_type === CT_TYPES.NULL,
        connectTimeoutSec: resolveTimerSeconds(
            vm.sfVersion,
            vm.device_serie,
            SOFTWARE_VERSION_SIX_TIMER.CONNECT,
            vm.timeout,
            false,
        ),
        selectTimeoutSec: resolveTimerSeconds(
            vm.sfVersion,
            vm.device_serie,
            SOFTWARE_VERSION_SIX_TIMER.SELECT,
            vm.timeout,
            false,
        ),
        // `screenValue` is ignored on the payment path — the cap comes from
        // vm.timeout — but it is passed for symmetry with the two above.
        paymentTimeoutSec: resolveTimerSeconds(
            vm.sfVersion,
            vm.device_serie,
            SOFTWARE_VERSION_SIX_TIMER.SELECT,
            vm.timeout,
            true,
        ),
        language_code: vm?.language_code ?? 'uk',
        isFeedbackEnabled: vm.feedbacks === 1,
        hasFiscalReceipt: vm.fisc_receipts === 1,
        treatsSessionClosedAsSale: false,
    };

    result.isCT10 =
        vm.ct_type === CT_TYPES.CT10 ||
        (vm.ct_type === CT_TYPES.NULL && vm.type_id === MACHINE_TYPES.FRIDGE);

    result.isCT5 =
        vm.ct_type === CT_TYPES.CT5 &&
        AVAILABLE_CT5_VM_TYPES.includes(vm.type_id);

    result.treatsSessionClosedAsSale =
        result.isCT5 &&
        isFirmware6x(vm.sfVersion) &&
        vm.payment_provider_id === PAYMENT_PROVIDER.TRANZZO;

    return result;
};
